import React, { useState, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Menu, Terminal, Activity, AlertTriangle, CalendarClock } from 'lucide-react';
import Sidebar from './Sidebar';
import AlertBanner from './AlertBanner';
import EventNotifier from './EventNotifier';
import InstallPrompt from './InstallPrompt';
import WelcomeTour from './WelcomeTour';
import { LOGO } from '../mock/data';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { setPrefsCache } from '../utils/prefs';
import api from '../api/client';

const OPERATOR_TABS = [
  { to: '/operator-terminal', tkey: 'nav.terminal', icon: Terminal },
  { to: '/machine-logs', tkey: 'nav.logs', icon: Activity },
  { to: '/problems', tkey: 'nav.problems', icon: AlertTriangle },
  { to: '/scheduling', tkey: 'nav.scheduling', icon: CalendarClock },
];

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useI18n();

  // Cache the org timezone/country app-wide so all pages format times consistently.
  useEffect(() => { api.getPreferences().then(setPrefsCache).catch(() => {}); }, []);
  const isOperator = user?.role === 'OPERATOR';

  return (
    <div className="flex min-h-screen kmt-grid-bg">
      <EventNotifier />
      <InstallPrompt />
      <WelcomeTour />
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

      {mobileOpen && (
        <div className="fixed inset-0 z-30 md:hidden" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setMobileOpen(false)} />
      )}

      <main className={`flex-1 min-w-0 overflow-x-hidden ${isOperator ? 'kmt-has-bottombar' : ''}`}>
        {/* mobile top bar */}
        <div className="md:hidden flex items-center gap-3 h-14 px-4 sticky top-0 z-20 kmt-no-print"
          style={{ background: 'rgba(10,10,11,0.92)', backdropFilter: 'blur(8px)', borderBottom: '1px solid var(--kmt-border)' }}>
          <button onClick={() => setMobileOpen(true)} aria-label="Open menu"
            className="w-9 h-9 rounded-md flex items-center justify-center kmt-panel-2" style={{ color: 'var(--kmt-gold)' }}>
            <Menu size={18} />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="KMT" className="w-6 h-6 object-contain" />
            <span className="font-display font-bold" style={{ letterSpacing: '0.12em', color: 'var(--kmt-gold)' }}>KMT</span>
          </div>
        </div>

        <AlertBanner />

        <Outlet />
      </main>

      {/* Operator phone-style bottom tab bar */}
      {isOperator && (
        <nav className="kmt-bottombar md:hidden">
          {OPERATOR_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink key={tab.to} to={tab.to} className={({ isActive }) => (isActive ? 'active' : '')}>
                <Icon size={20} strokeWidth={1.9} />
                <span className="truncate px-1" style={{ maxWidth: 80 }}>{t(tab.tkey)}</span>
              </NavLink>
            );
          })}
        </nav>
      )}
    </div>
  );
}
